/**
 * Plain-English explanations of each palette role. Used by PaletteStep so
 * the operator (and the client they're showing the wizard to) understands
 * what each token actually controls.
 */
export interface PaletteRoleCopy {
  /** Token role / display name. */
  name: string;
  /** What it controls in plain English. */
  blurb: string;
  /** When you'd want to override the vibe default. */
  whenToOverride: string;
}

export const PALETTE_COPY = {
  accent: {
    name: 'Accent',
    blurb:
      'The single saturated color used for buttons, links, and small attention-grabbers. The one piece of color the visitor remembers.',
    whenToOverride:
      'When the client has a brand color you must match (a corporate red, a team blue, etc.) or when the vibe default clashes with their existing materials.',
  } satisfies PaletteRoleCopy,
  ink: {
    name: 'Ink',
    blurb:
      "The primary text color across the site. Under the Nuzum Method this is auto-derived from the obsidian / paper base and shouldn't usually need overriding.",
    whenToOverride:
      "Almost never. If you find yourself wanting to override ink, you probably want a different vibe instead.",
  } satisfies PaletteRoleCopy,
  muted: {
    name: 'Muted',
    blurb: 'Secondary text - subtitles, captions, metadata. Quieter than ink, still WCAG-AA legible.',
    whenToOverride:
      "Almost never. The signature owns the ink/muted relationship to keep contrast safe automatically.",
  } satisfies PaletteRoleCopy,
  warmTones: {
    name: 'Warm tones',
    blurb:
      'Warm-leaning support colors used for surface variants and seasonal flourishes. Under the Nuzum Method these mostly inform the bone-paper backgrounds of light sections.',
    whenToOverride:
      "When you want a specific warm color (a particular cream, a particular ivory) that the vibe default doesn't quite hit.",
  } satisfies PaletteRoleCopy,
  coolTones: {
    name: 'Cool tones',
    blurb:
      "Cool-leaning support colors. Under the Nuzum Method these mostly inform the obsidian / moss base variants.",
    whenToOverride:
      "Rarely - the signature tunes obsidian + moss intentionally. Override only if a client has a specific corporate cool you must hit.",
  } satisfies PaletteRoleCopy,
  surfaceVariants: {
    name: 'Surface variants',
    blurb:
      "Background tones for cards and elevated surfaces. Under the Nuzum Method, surface choice is largely automatic per shell.",
    whenToOverride:
      "Almost never under the Nuzum Method. These were meaningful under the legacy flat-vibe rendering.",
  } satisfies PaletteRoleCopy,
} as const;

export type PaletteRole = keyof typeof PALETTE_COPY;
